/** First day (6): the round trip. Ask, see it change, make it live. Board learn-r2, missions 01 to 06. */
import type { Mission } from '../missions'
import { FLAG_US, GOOD_ASK, savedOnly, selfTick } from './shared'

export const firstDay: Mission[] = [
  {
    id: 'talk-to-your-agent',
    order: 1,
    title: 'Talk to your agent',
    track: 'first-day',
    say: 'What is this app, and what can you change in it for me?',
    happens: [
      "Your agent reads the app's guide (AGENTS.md) and its skills.",
      'It tells you in plain words what the app does and which screens it has.',
      "From its limits skill, it says what it can change for you, what it won't touch (sign-in, keys, the house rules, your app's history), and how your plan's usage limits work.",
      "Ask a second question about anything that isn't clear.",
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: 'The answer is full of code words.', say: "Say that again in plain words, for someone who doesn't read code." },
      { symptom: "The agent says it can't find the app.", say: "Open this app's folder and read AGENTS.md first." },
      { symptom: 'The agent stopped and says you hit a limit.', say: 'Which limit did we hit, when does it reset, and what are my choices?' },
      FLAG_US,
    ],
    how: selfTick('Talk to your agent', "This page can't see your chat."),
    skill: 'limits',
    recipe: 'what-your-agent-can-do',
    lesson: GOOD_ASK,
  },
  {
    id: 'open-your-preview',
    order: 2,
    title: 'Open your preview',
    track: 'first-day',
    say: 'Start a preview of my app and send me the link.',
    happens: [
      'Your agent starts the dev server on your cpg.computer computer.',
      'A Cloudflare Tunnel already points your preview address at it: https://<your-brand>-preview.cpg.computer.',
      'The agent sends you the link. Open it on your phone or laptop and sign in.',
      'Open this mission there. A Preview tag shows beside your brand, and the mission ticks.',
    ],
    check: { kind: 'host' },
    watching: "This page's address, waiting for one that ends in -preview.cpg.computer.",
    ifNot: [
      { symptom: "The link doesn't load.", say: 'Is the dev server running? Start it again and check that my preview link answers.' },
      { symptom: "It says you don't have access.", say: "I can't open my preview: it says I don't have access. What should I check?" },
      { symptom: 'The link opened, but this mission still waits.', say: "Send me my preview link again. I think I'm on my live address." },
      FLAG_US,
    ],
    how: `The page reads its own address. Nothing is sent anywhere to check it. ${savedOnly('Open your preview')}`,
    skill: 'preview',
    recipe: 'look-before-its-live',
  },
  {
    id: 'edit-this-page',
    order: 3,
    title: 'Edit this page',
    track: 'first-day',
    say: "Change the heading on the Learn page to 'Field Day runs on cpg.computer'.",
    happens: [
      'Your agent opens src/learn/page.ts, the one Learn file that is yours.',
      'It changes the heading and saves the file.',
      'The dev server sends the change to this page through hot reload. No refresh.',
      'The heading at the top of Learn changes, and this mission ticks itself.',
    ],
    check: { kind: 'value', key: 'heading' },
    watching: 'The heading in src/learn/page.ts, against the first one this browser saw.',
    ifNot: [
      { symptom: 'Nothing changed after a minute.', say: 'Is the preview running? Start it again and send me the link.' },
      { symptom: 'The agent changed a different heading.', say: 'Change the heading in src/learn/page.ts instead, and put the other one back.' },
      { symptom: "You're on your live address, not the preview.", say: 'Send me my preview link.' },
      FLAG_US,
    ],
    how: `The first time you open this mission, this browser remembers the heading. When the file changes, the dev server swaps the new value in and the page compares the two. ${savedOnly('Edit this page')}`,
    skill: 'learn',
    recipe: 'look-before-its-live',
  },
  {
    id: 'undo-a-change',
    order: 4,
    title: 'Undo a change',
    track: 'first-day',
    say: 'Undo that last change.',
    happens: [
      'Your agent finds the change it made to src/learn/page.ts.',
      "It follows its undo skill: it says what it will put back and waits for your yes. Not committed yet: it restores the file. Already committed or shipped: it makes a new commit that reverses it (git revert). It never rewrites history.",
      'Hot reload brings the old heading back to this page.',
      'The heading is the one you started with again, and the mission ticks.',
    ],
    check: { kind: 'value', key: 'heading', undo: true, from: 'edit-this-page' },
    watching: 'The heading in src/learn/page.ts: waiting for it to change, then go back to the one you started with.',
    ifNot: [
      { symptom: "The heading hasn't changed yet.", say: "Change the heading on the Learn page to 'Field Day runs on cpg.computer'." },
      { symptom: 'The agent undid other things too.', say: 'Undo only the heading change in src/learn/page.ts, and put the rest back.' },
      { symptom: 'The agent wants to reset or force-push.', say: "Don't rewrite history. Make a new commit that reverses the change." },
      FLAG_US,
    ],
    how: `This browser remembers the heading from "Edit this page". The mission ticks when the heading has changed and then come back. ${savedOnly('Undo a change')}`,
    skill: 'undo',
    recipe: 'undo',
  },
  {
    id: 'ship-it',
    order: 5,
    title: 'Ship it',
    track: 'first-day',
    say: 'Ship it.',
    happens: [
      'Your agent runs bun run check: types, tests, the house rules and the build.',
      'It commits the change with a plain message and pushes it to GitHub.',
      'Railway sees the push, builds your app and starts the new version. That takes a minute or two.',
      'Your live address runs the same version as your preview.',
    ],
    check: { kind: 'version' },
    watching: "Your preview's newest saved version (GET /api/dev/version), against the version your live address reports at /healthz. It ticks when live reaches it.",
    ifNot: [
      { symptom: 'The check fails.', say: 'Tell me in plain words what failed, fix it, and ship again.' },
      { symptom: 'The page says to open it on your preview.', say: 'Send me my preview link.' },
      { symptom: "It's been more than five minutes.", say: 'Is the deploy finished? Read the deploy logs and tell me what they say.' },
      { symptom: 'Your live address still shows the old version.', say: 'Which version is my live app running, and which one did you push?' },
      FLAG_US,
    ],
    how: `Open this mission on your preview. Every few seconds it asks your computer for its newest saved version, and asks your live address which version it runs. When live runs that version (or a newer one with it inside, if a teammate shipped too), it's shipped. Only version numbers travel, between your own two addresses. ${savedOnly('Ship it')}`,
    skill: 'ship',
    recipe: 'make-it-live',
  },
  {
    id: 'find-anything',
    order: 6,
    title: 'Find anything',
    track: 'first-day',
    act: 'do',
    say: "Press ⌘K (Ctrl K on Windows), or tap Search on your phone. Type a screen's name and jump to it.",
    happens: [
      'The search box opens over the page.',
      'It lists your screens, the Learn pages and a few actions.',
      'Type a few letters. The list narrows as you type.',
      'Press Enter or tap a result to jump there.',
    ],
    check: { kind: 'ui', event: 'palette-jump' },
    watching: 'The search box, waiting for you to jump to a screen with it.',
    ifNot: [
      { symptom: 'Nothing opens.', say: 'The search box (⌘K) does nothing on my app. Can you check it?' },
      FLAG_US,
    ],
    how: `The page notices a jump made from the search box. Nothing you type is saved. ${savedOnly('Find anything')}`,
    recipe: 'find-anything',
    needs: "The search box works now. The check that sees you use it comes in a Learn update.",
  },
]
